import { Tab as MuiTab, Tabs as MuiTabs } from '@mui/material'
import { includes } from 'lodash'
import { generatePath, Navigate, useNavigate, useParams } from 'react-router-dom'

import Paper from 'components/Paper'
import { useUrlSearchParams } from 'providers/router/hooks/useUrlSearchParams'
import { ROUTES } from 'routes'
import { SectionTab, SectionTabsProps, SectionTabsUrlParams } from './types'

export const SectionTabs = <T extends string>({
  path,
  sections,
  stickyTop,
}: SectionTabsProps<T>): React.JSX.Element => {
  const navigate = useNavigate()
  const { urlParams } = useUrlSearchParams()
  const { id, section: urlSection, tab: urlTab, workspaceId } = useParams<SectionTabsUrlParams<T>>()

  if (
    !urlSection ||
    !includes(
      sections.map((t: SectionTab<T>): T => t.element),
      urlSection,
    )
  ) {
    return <Navigate to={ROUTES.BASE.NOT_FOUND} />
  }

  const handleTabClick = (section: SectionTab<T>): void => {
    navigate({
      pathname: generatePath(path, {
        id,
        section: section.element,
        tab: urlTab,
        workspaceId,
      }),
      search: urlParams,
    })
  }

  return (
    <Paper
      spacing='none'
      sx={{
        height: 'fit-content',
        ml: 2,
        p: 3,
        ...(stickyTop !== undefined
          ? {
              position: 'sticky',
              top: `${stickyTop}px`,
            }
          : {}),
      }}
    >
      <MuiTabs
        orientation='vertical'
        sx={{
          '.MuiTab-root': {
            alignItems: 'flex-start',
          },
          '.MuiTabs-flexContainerVertical': {
            alignItems: 'flex-start',
          },
          '.MuiTabs-indicator': {
            display: 'none',
          },
          width: 'max-content',
        }}
        value={urlSection}
      >
        {sections.map((section: SectionTab<T>): React.JSX.Element => {
          const { disabled, element, label } = section

          return (
            <MuiTab
              data-cy={element}
              disabled={disabled}
              key={element}
              label={label}
              onClick={(): void => handleTabClick(section)}
              value={element}
            />
          )
        })}
      </MuiTabs>
    </Paper>
  )
}
